<!-- eslint-disable vuejs-accessibility/no-static-element-interactions -->
<template>
  <div
    ref="productCard"
    class="product-card"
    :class="[
      `product-card--${cardLayout}`,
      `product-card${imageBackgroundColor}`,
      {
        [$class('loading')]: !finalProduct || loadingState || finalProduct?.loading,
        'animatable': renderCarouselAnimation,
        'product-card--flat': enableFlatImage || enableVariantImage,
        'product-card--padded-images': paddedImages,
        'product-card--searchspring': getShopifyProduct && searchspringProduct && !shopifyProduct,
        'show-secondary-image': showSecondaryImage,
        'dickey-bundle-product': isDickeyBundle,
      }
    ]"
    :data-dy-product-id="dyStrategyId ? selectedProduct.variants[0]?.sku : null"
    :data-dy-strategy-id="dyStrategyId"
    role="group"
    :aria-label="$string('accessibility.product.card_label')"
    @mouseenter="handleFocusIn"
    @mouseleave="handleMouseLeave"
    @focusin="handleFocusIn"
    @focusout="handleFocusOut"
  >
    <div
      v-if="enableImageCarousel"
      class="product-card__carousel-container"
      @blur="() => null"
    >
      <div
        v-if="showWishlist && isRecommendation"
        class="product-card__carousel-container-icon"
      >
        <wishlist-button
          v-show="renderWishlistBtn"
          class="product-card__wishlist"
          :product="finalProduct"
          template="card"
          :variant="finalProduct?.selectedOrFirstAvailableVariant"
        />
      </div>

      <component
        :is="imageIsLinked && selectedProduct?.url ? 'a' : 'div'"
        ref="carousel"
        :href="selectedProduct?.url"
        class="product-card__carousel"
        role="region"
        :aria-label="$string('accessibility.carousel.make_it_a_set')"
        @scroll="handleCarouselScroll(true)"
        @click="handleImageHrefClick"
      >
        <span
          class="visually-hidden"
          v-text="finalProduct?.title"
        />

        <span
          v-if="preorder"
          class="product-card__preorder text-body-3xs-regular"
        >
          <span v-text="$string('product.preorder.card')" />
        </span>

        <template
          v-for="(image, index) of productCarouselImages"
          :key="`${finalProduct?.id}-image-${index}`"
        >
          <responsive-image
            ref="cards"
            class="product-card__carousel-image"
            :image="image"
            :lazyload="!interactedWith && index > 1"
            :alt="getSlideAlt(image, index)"
            ratio="3:4"
            :sizes="imageSizes"
            role="group"
            :aria-label="`slide ${index + 1} of ${productCarouselImages.length}`"
            :aria-hidden="index !== currentCarouselIndex"
            :tabindex="index === currentCarouselIndex ? 0 : -1"
          />
        </template>

        <div
          v-if="!productCarouselImages?.length"
          class="product-card__carousel-image product-card__carousel-image--none"
        />
      </component>

      <template v-if="productCarouselImages?.length > 1">
        <button
          ref="prev"
          class="product-card__carousel-btn product-card__carousel-btn--prev is-disabled"
          type="button"
          disabled="true"
          @click="moveProductCarousel('prev')"
        >
          <span
            class="visually-hidden"
            v-text="$string('accessibility.carousel.previous')"
          />
          <icon-arrow-left />
        </button>

        <button
          ref="next"
          class="product-card__carousel-btn product-card__carousel-btn--next"
          type="button"
          @click="moveProductCarousel('next')"
        >
          <span
            class="visually-hidden"
            v-text="$string('accessibility.carousel.next')"
          />
          <icon-arrow-right />
        </button>
      </template>

      <div
        v-if="(shopifyProduct || product) && showInfo && showQuickAdd && cardLayout !== 'center'"
        class="product-card__quick-add"
        :style="quickAddStyles"
        :class="{
          [$class('active')]: productWasAddedToCart,
          [$class('open')]: showQuickAtc,
          [$class('disabled')]: isShippingUnavailable(),
        }"
      >
        <button
          v-show="!showBackInStock"
          class="product-card__quick-add-button"
          :aria-label="$string('product.quick_add.open')"
          :class="{ [$class('active')]: productWasAddedToCart }"
          type="button"
          @click.prevent="handleAtcEvent"
        >
          <transition name="fade">
            <component
              :is="addToCartIcon"
              class="product-card__quick-add-icon"
              :class="{ [$class('active')]: showAddToCart && !productWasAddedToCart }"
            />
          </transition>
        </button>
      </div>

      <quick-add-to-cart
        v-if="showQuickAtc"
        :product="selectedProduct"
        :bis-settings="{
          showCloseIcon: true,
          namespace: `product-card-${finalProduct?.id}`
        }"
        :product-card-location="productCardLocation"
        :final-sale="finalSale"
        @bis-active="handleBackInStockModal"
        @form-submitted="handleDynamicYieldOverlay"
      />
    </div>

    <div
      v-else
      class="product-card__image-container"
      :data-button="customAtc"
      @focusin="handleSecondaryImage"
      @mouseenter="handleSecondaryImage"
      @mouseleave="handleMouseLeave"
      @blur="() => null"
    >
      <div
        v-if="showWishlist && isRecommendation"
        class="product-card__image-container-icon"
      >
        <wishlist-button
          v-show="renderWishlistBtn"
          class="product-card__wishlist"
          :product="finalProduct"
          template="card"
          :variant="finalProduct?.selectedOrFirstAvailableVariant"
        />
      </div>

      <component
        :is="(selectedProduct?.url && !isDickeyBundle) ? 'a' : 'div'"
        :href="selectedProduct?.url"
        @click="emitProductClick"
      >
        <span
          v-if="preorder"
          class="product-card__preorder text-body-3xs-regular"
        >
          <span v-text="$string('product.preorder.card')" />
        </span>

        <responsive-image
          v-if="enableVariantImage || enableFlatImage"
          class="product-card__primary-image"
          :image="productSpecialImage"
          :lazyload="false"
          :ratio="source === 'five-block-section' ? '720:877' : '3:4'"
          :source="source"
          :sizes="imageSizes"
        />

        <responsive-image
          v-else
          class="product-card__primary-image"
          :image="selectedProduct?.featuredImage?.image || selectedProduct?.featuredImage"
          :lazyload="false"
          :ratio="source === 'five-block-section' ? '720:877' : '3:4'"
          :source="source"
          :sizes="imageSizes"
        />

        <responsive-image
          v-if="
            loadSecondaryImage &&
              (selectedProduct?.secondaryImage?.image || selectedProduct?.secondaryImage)
          "
          class="product-card__secondary-image"
          :image="selectedProduct?.secondaryImage"
          :alt="`${selectedProduct?.secondaryImage.alt} - ${$string('accessibility.view')} 2`"
          :ratio="source === 'five-block-section' ? '720:877' : '3:4'"
          :source="source"
          :padding="false"
          :sizes="imageSizes"
          @image-loaded="showSecondaryImage = true"
        />
      </component>

      <div
        v-if="(shopifyProduct || product) && showInfo && showQuickAdd"
        :class="{ [$class('active')]: productWasAddedToCart }"
        class="product-card__quick-add"
        :style="quickAddStyles"
      >
        <button
          v-show="!showBackInStock"
          class="product-card__quick-add-button"
          :class="{
            [$class('active')]: productWasAddedToCart,
          }"
          :aria-label="$string('product.quick_add.open')"
          type="button"
          @click.prevent="handleAtcEvent"
        >
          <transition name="fade">
            <component
              :is="addToCartIcon"
              class="product-card__quick-add-icon"
              :class="{ [$class('active')]: showAddToCart && !productWasAddedToCart }"
            />
          </transition>
        </button>
      </div>

      <quick-add-to-cart
        v-if="showQuickAtc"
        :product="selectedProduct"
        :bis-settings="{
          showCloseIcon: true,
          namespace: `product-card-${finalProduct?.id}`
        }"
        :product-card-location="productCardLocation"
        :final-sale="finalSale"
        @bis-active="handleBackInStockModal"
        @form-submitted="handleDynamicYieldOverlay"
      />
    </div>

    <product-badge
      v-if="showInfo && showPreorderBadge"
      :product="selectedProduct"
      badge-class="text-body-3xs-regular product-card__preorder-badge"
      badge-element="p"
      badge-type="preorderBadge"
    />

    <div
      v-if="showInfo"
      class="product-card__footer"
    >
      <ul
        v-if="showBadges"
        class="product-card__badges"
      >
        <product-badge
          :product="selectedProduct"
          badge-class="text-body-3xs-regular"
          badge-element="li"
          badge-type="topBadge"
        />

        <product-badge
          :product="selectedProduct"
          badge-class="text-body-3xs-regular"
          badge-element="li"
          badge-type="bottomBadge"
        />
      </ul>

      <div
        v-if="showProductContent"
        class="product-card__footer-content"
        :class="{ 'wishlist-disabled': !showWishlist }"
      >
        <component :is="headingElement">
          <component
            :is="selectedProduct?.url ? 'a' : 'p'"
            class="product-card__title"
            :class="getTitleClasses()"
            :href="selectedProduct?.url"
            @click="handleHrefClick"
            v-text="selectedProduct?.title"
          />
        </component>

        <product-badge
          v-if="showSecondaryName"
          :product="selectedProduct"
          badge-class="product-card__subtitle"
          badge-element="div"
          badge-type="secondaryName"
        />

        <product-prices
          v-if="showPricing && selectedProduct && typeof selectedProduct.price !== 'undefined'"
          class="product-card__prices"
          :class="`align-${cardContentAlignment}`"
          :available="selectedProduct?.available"
          :compare-at-price="selectedProduct?.compareAtPrice?.amount"
          :price="selectedProduct?.price?.amount"
          :varies="selectedProduct?.price?.varies"
          :final-sale="finalSale"
          location="product-card"
          class-price="text-body-2xs-regular"
          class-compare-at-price="text-body-2xs-regular"
        />
      </div>

      <div
        v-if="showWishlist && !isRecommendation"
        class="product-card__footer-icon"
      >
        <wishlist-button
          v-show="renderWishlistBtn"
          class="product-card__wishlist"
          :product="finalProduct"
          template="card"
          :variant="finalProduct?.selectedOrFirstAvailableVariant"
        />
      </div>

      <product-swatches
        v-if="renderSwatches"
        :active-merge="activeMerge"
        block-class="product-card"
        :card-index="cardIndex"
        :card-layout="cardLayout"
        :product="sortArrayByActiveSwatch"
        :product-link="selectedProduct?.url"
        @update-merge="handleUpdateMerge"
        @update-product-id="handleUpdateProductId"
      />

      <div
        v-if="showBadges"
        class="product-card__specialty-badges"
      >
        <product-badge
          :product="selectedProduct"
          badge-class="font-family-freight-italic product-card__specialty-badge"
          badge-element="p"
          badge-type="specialtyBadge"
        />
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Product card (product-card)
 * -----------------------------------------------------------------------------
 * Product card used throughout the site.
 *
 * @param {Boolean|Object} product - Product object, false shows loading state.
 * @param {Boolean|Object} searchspringProduct - SS product object.
 * @param {String} cardLayout - Size of product card to be displayed, accepts
 * values 'small', 'regular', 'large' or 'center'.
 * @param {Number} cardIndex - Index of product card in for loop.
 * @param {Number} carouselLimit - Limit of images to be displayed in carousel
 * @param {Number} customAtc - If a custom add to cart button is needed
 * @param {Boolean} enableFlatImage - Shows product flat images. Flat images
  will not display a secondary image or be included in product carousel.
 * @param {Boolean} enableVariantImage - Shows product variant image first.
  Will not display a secondary image or be included in product carousel.
 * @param {Boolean} enableImageCarousel - Shows carousel of product images
 * in card.
 * @param {Boolean} enableSecondaryImage - Shows secondary image on product
 * hover
 * @param {Boolean} getShopifyProduct - Get Shopify product data on init
 * @param {Number} faceoutIndex - Index for image to be used as first image
 * in product carousel.
 * @param {String} imageBackgroundColor - Background color for images on
 * product card.
 * @param {Boolean} imageIsLinked - If card should link to a PDP.
 * @param {Boolean} [isAnimatable] - If card should animate.
 * @param {Boolean} loadingState - Sets loading state for product card.
 * @param {Boolean} paddedImages - Adds padding around product image.
 * @param {Boolean} showBadges - Shows all badges in product card.
 * @param {Boolean} showFinalSale - Shows final sale tag in product price.
 * @param {Boolean} showInfo - Shows the product information.
 * @param {Boolean} showPreorderBadge - Shows preorder badges in product card.
 * @param {Boolean} showPricing - Shows pricing in product card.
 * @param {Boolean} showProductContent - Displays product footer content.
 * @param {Boolean} showSecondaryName - Displays product secondary name from
 * metafield.
 * @param {Boolean} showSwatches - Displays color swatches on product card.
 * @param {Boolean} showWishlist - Displays wishlist icon.
 * @param {Boolean} showQuickAdd - Enables quick add on product card.
 * @param {Boolean} customAtc - Enable custom add to cart handler. When true
 * component emits 'custom-quick-add' event, which can then be observed
 * within the parent component calling product card
 * @param {String} headerElement - the HTML element to render the product title
 * within
 */
// eslint-disable-next-line import/named
import { defineAsyncComponent } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'

import ProductBadge from '~global/product-badge/product-badge'
import ProductPrices from '~global/product-prices/product-prices'
import ProductSwatches from '~global/product-swatches/product-swatches'

import ResponsiveImage from '~global/images/responsive-image'
import WishlistButton from '~async/wishlist-button/wishlist-button'

import IconArrowLeft from '~icons/directional-navigation/arrow/left-small.svg'
import IconArrowRight from '~icons/directional-navigation/arrow/right-small.svg'
import IconCheck from '~icons/general/check.svg'
import IconClose from '~icons/directional-navigation/close.svg'
import IconPlus from '~icons/directional-navigation/plus.svg'
import IconWishlist from '~icons/general/wishlist.svg'

import { isVisible, setInitialActiveMerge } from '~/helpers/general'
import { shopifyProductByIdQuery } from '~/helpers/shopify'

export default {
  name: 'ProductCard',

  components: {
    IconArrowLeft,
    IconArrowRight,
    IconCheck,
    IconClose,
    IconPlus,
    IconWishlist,
    ProductBadge,
    ProductPrices,
    ProductSwatches,
    QuickAddToCart: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.quick-add-to-cart' */
        '~async/quick-add-to-cart/quick-add-to-cart'
      ),
    }),
    ResponsiveImage,
    WishlistButton,
  },

  props: {
    isRecommendation: {
      type: Boolean,
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
    },
    searchspringProduct: {
      type: [Boolean, Object],
      default: false,
    },
    cardLayout: {
      type: String,
      default: 'regular',
    },
    cardIndex: {
      type: Number,
      default: null,
    },
    carouselLimit: {
      type: Number,
      default: null,
    },
    customAtc: {
      type: Boolean,
      default: false,
    },
    enableFlatImage: {
      type: Boolean,
      default: false,
    },
    enableVariantImage: {
      type: Boolean,
      default: false,
    },
    enableImageCarousel: {
      type: Boolean,
      default: false,
    },
    enableSecondaryImage: {
      type: Boolean,
      default: true,
    },
    faceoutIndex: {
      type: Number,
      default: null,
    },
    getShopifyProduct: {
      type: Boolean,
      default: false,
    },
    imageBackgroundColor: {
      type: String,
      default: '--color-brand-white',
    },
    imageIsLinked: {
      type: Boolean,
      default: true,
    },
    isAnimatable: {
      type: Boolean,
      default: true,
    },
    loadingState: {
      type: Boolean,
      default: false,
    },
    paddedImages: {
      type: Boolean,
      default: false,
    },
    showBadges: {
      type: Boolean,
      default: true,
    },
    showFinalSale: {
      type: Boolean,
      default: true,
    },
    showInfo: {
      type: Boolean,
      default: true,
    },
    showPreorderBadge: {
      type: Boolean,
      default: true,
    },
    showPricing: {
      type: Boolean,
      default: true,
    },
    showProductContent: {
      type: Boolean,
      default: true,
    },
    showSecondaryName: {
      type: Boolean,
      default: true,
    },
    showSwatches: {
      type: Boolean,
      default: true,
    },
    showWishlist: {
      type: Boolean,
      default: true,
    },
    showQuickAdd: {
      type: Boolean,
      default: true,
    },
    dyStrategyId: {
      type: Number,
      default: null,
    },
    productCardLocation: {
      type: Object,
      default: null,
    },
    showUpdatedSuccess: {
      type: Boolean,
      default: false,
    },
    isDickeyBundle: {
      type: Boolean,
      default: false,
    },
    source: {
      type: String,
      default: '',
    },
    fontSettings: {
      type: String,
      default: '',
    },
    cardContentAlignment: {
      type: String,
      default: '',
    },
    headingElement: {
      type: String,
      default: 'h3',
      validator(val) {
        return ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(val)
      },
    },
  },

  emits: {
    'custom-quick-add': () => true,
  },

  data() {
    return {
      activeMerge: {},
      addToCartOverlayOpen: false,
      interactedWith: false,
      intersected: false,
      legacyGroup: false,
      loadSecondaryImage: false,
      productWasAddedToCart: false,
      showAddToCart: false,
      showSecondaryImage: false,
      selectedProduct: {
        ...this.product || this.searchspringProduct,
        images: (this.product || this.searchspringProduct)?.images ||
          (this.product || this.searchspringProduct)?.media?.filter(
            val => val.mediaType === 'image',
          ).map(val => val.image),
      },
      shopifyProduct: null,
      shopifyProductRefetched: false,
      showBackInStock: false,
      firstActiveMergeName: null,
      currentCarouselIndex: 0,
      isCardSelected: false,
    }
  },

  computed: {

    /**
     * Sort product swatches array by active swatch.
     * @returns {Object}
     */
    sortArrayByActiveSwatch() {
      if (!this.selectedProduct?.grouping) {
        return this.selectedProduct
      }

      if (this.firstActiveMergeName) {
        const newSelectedProduct = { ...this.selectedProduct }

        newSelectedProduct.grouping = newSelectedProduct.grouping.sort((object) => {
          if (object?.swatch?.name?.toLowerCase() === this.firstActiveMergeName?.toLowerCase()) {
            return -1
          }

          return 1
        })

        return newSelectedProduct
      }

      return this.selectedProduct
    },

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      activeOverlay: (state) => state.overlays.active,
      isMobile: (state) => state.index.screen.mobile,
      lastAddedProductId: (state) => state.quickAddToCart.lastAddedProductId,
    }),

    /**
     * Compute active merge name.
     * @returns {String}
     */
    activeMergeName() {
      return this.activeMerge?.swatch?.name
    },

    /**
     * The shopify product or the provided product
     */
    finalProduct() {
      return this.shopifyProduct || this.product || this.searchspringProduct
    },

    /**
     * Compute product card flat image.
     * @returns {Number}
     */
    flatImageIndex() {
      const productImages = this.getProductImages(this.selectedProduct)

      if (!productImages.length) {
        return 0
      }

      if (this.selectedProduct?.metafields?.flatImageIndex < 0) {
        return productImages?.length + this.selectedProduct?.metafields?.flatImageIndex
      }

      if (this.selectedProduct?.metafields?.flatImageIndex >= productImages?.length) {
        return productImages?.length - 1
      }

      return this.selectedProduct?.metafields?.flatImageIndex
    },

    /**
     * Compute final sale state based on product tags.
     * @returns {Boolean}
     */
    finalSale() {
      if (!this.showFinalSale) {
        return false
      }

      const tags = this.selectedProduct?.tags

      return tags?.some((tag) => {
        return (
          tag.toLowerCase().includes('final sale') ||
          tag.toLowerCase().includes('final_sale') ||
          tag.toLowerCase().includes('finalsale')
        )
      })
    },

    /**
     * Compute image sizes.
     */
    imageSizes() {
      return this.cardLayout === 'regular'
        ? '(min-width: 1024px) 25vw, 100vw'
        : '(min-width: 1024px) 25vw, 50vw'
    },

    /**
     * Compute if preorder badge should be displayed.
     * - A majority of variant SKUs need to be preorder before the badge is
     *   displayed.
     * @returns {Boolean}
     */
    preorder() {
      const threshold = Math.ceil(this.finalProduct?.variants?.length / 2)
      let count = 0

      this.finalProduct?.variants?.forEach((variant) => {
        if (!this.finalProduct?.preorder?.skus?.includes(variant.sku)) {
          return
        }

        count += 1
      })

      return count >= threshold
    },

    /**
     * Compute images to display in product carousel based on carousel limit.
     * @returns {Array}
     */
    productCarouselImages() {
      let productImages = this.getProductImages(this.selectedProduct)

      if (!productImages) {
        return []
      }

      if (this.faceoutIndex) {
        let faceoutIndex = this.faceoutIndex

        if (this.faceoutIndex < 0) {
          faceoutIndex = productImages?.length + this.faceoutIndex
        }

        if (this.faceoutIndex >= productImages.length) {
          faceoutIndex = productImages.length - 1
        }

        const faceoutImage = productImages.find(image => image.index === faceoutIndex)
        productImages = [
          faceoutImage,
          ...productImages.filter(image => image.index !== faceoutIndex),
        ]
      }

      if (this.enableVariantImage &&
        this.selectedProduct.selectedOrFirstAvailableVariant?.image?.url) {
        productImages = [
          this.selectedProduct.selectedOrFirstAvailableVariant?.image,
          ...productImages,
        ]
      }

      if (!this.carouselLimit ||
        this.carouselLimit === 0 ||
        productImages?.length < this.carouselLimit
      ) {
        return productImages
      }

      return productImages?.slice(0, this.carouselLimit)
    },

    /**
     * Get variant image for Loobook/Hotspot/Shop the Look sections
     * @returns {Array}
     */
    productSpecialImage() {
      const variantImage = this.selectedProduct.selectedOrFirstAvailableVariant?.image?.url
        ? this.selectedProduct.selectedOrFirstAvailableVariant?.image
        : this.selectedProduct?.featuredImage

      const flatImage = this.selectedProduct?.images[this.flatImageIndex || 0]

      if (this.enableVariantImage) {
        return variantImage
      }

      return flatImage
    },

    /**
     * Render swatches.
     * @returns {Boolean}
     */
    renderSwatches() {
      return this.showSwatches &&
        (this.selectedProduct?.grouping?.length > 1 ||
          this.selectedProduct?.legacySwatches?.length > 1)
    },

    /**
     * Return icon for add to cart button
     * @returns {String}
     */
    addToCartIcon() {
      if (this.productWasAddedToCart) {
        return 'icon-check'
      }

      return 'icon-plus'
    },

    /**
     * Return if carousel animation should play.
     * @returns {Boolean}
     */
    renderCarouselAnimation() {
      return this.isAnimatable &&
        this.cardIndex === 0 &&
        this.enableImageCarousel &&
        this.productCarouselImages?.length > 1
    },

    /**
     * Compute add to cart button display
     * @returns {Boolean}
     */
    showQuickAtc() {
      if (this.showBackInStock) {
        return true
      }

      return this.showInfo && !this.isMobile && this.showAddToCart
    },

    /**
     * Hides wishlist button from CA store
     */
    renderWishlistBtn() {
      return window.cnvs?.store.permanentDomain !== '//veronica-beard-canada.myshopify.com/'
    },

    /**
     * Compute quick add styles
     * @returns {Boolean}
     */
    quickAddStyles() {
      return {
        opacity: this.isMobile || this.isCardSelected ? '0.85' : '0',
      }
    },
  },

  watch: {

    activeMergeName(newActiveMergeName) {
      if (!this.firstActiveMergeName) {
        this.firstActiveMergeName = newActiveMergeName
      }
    },

    searchspringProduct: {
      handler() {
        this.fetchShopifyProduct()
      },
      immediate: true,
      deep: true,
    },

    lastAddedProductId: {
      async handler() {
        await this.$nextTick()
        this.checkProductWasAdded()
      },
      immediate: true,
    },

    getActiveOverlay() {
      if (this.addToCartOverlayOpen) {
        if (!this.activeOverlay.some((overlay) => overlay.namespace === 'quick-add-to-cart-modal')) {
          this.addToCartOverlayOpen = false
          this.showAddToCart = false
        }
      }
    },
  },

  mounted() {
    this.handleCarouselScroll()

    if (!this.getShopifyProduct) {
      this.setInitialActiveMerge()
    }

    this.setDataLoadObserver()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
      setQuickAddProduct: 'quickAddToCart/setQuickAddProduct',
      setShowUpdatedSuccess: 'quickAddToCart/setUpdatedSuccess',
      setSizeGuideProduct: 'sizeGuideDrawer/setSizeGuideProduct',
    }),

    getSlideAlt(image, index) {
      return this.$string('accessibility.carousel.slide_img_alt', {
        replace: {
          alt: image?.alt,
          number: index + 1,
          total: this.productCarouselImages?.length,
        },
      })
    },

    /**
     * Fetches Shopify data for the product.
     * @param {Number} id - Product ID to load.
     * @param {Boolean} update - Update existing product.
     */
    async fetchShopifyProduct(id = this.searchspringProduct.id, update = false) {
      if (
        !update &&
        (
          this.shopifyProduct ||
          !this.intersected ||
          !this.getShopifyProduct ||
          !id
        )
      ) {
        return
      }

      await this.queryShopifyAndUpdateProduct(id, update ? false : 300000)

      this.selectedProduct = {
        ...this.shopifyProduct,
        images: this.shopifyProduct?.images ||
          this.shopifyProduct?.media?.filter((val) => val.mediaType === 'image')
            .map(val => val.image),
      }

      this.setInitialActiveMerge()
    },

    /**
     * Gets products data from Shopify with optional caching.
     * Updates internal product data to match result.
     * @param {Number} id - Product ID to load.
     * @param {Boolean|Number} useCache - Cache duration in ms.
     */
    async queryShopifyAndUpdateProduct(id = this.searchspringProduct.id, useCache = false) {
      const queryId = typeof id === 'string' && id?.includes('gid://shopify')
        ? id
        : `gid://shopify/Product/${id}`

      const product = await shopifyProductByIdQuery(queryId, useCache)

      if (!product) {
        return
      }

      this.shopifyProduct = product
    },

    /**
     * Fetches Shopify product without cache.
     */
    async refetchShopifyProduct() {
      if (this.getShopifyProduct && !this.shopifyProductRefetched) {
        this.shopifyProductRefetched = true

        await this.queryShopifyAndUpdateProduct(this.selectedProduct.id)
        await this.$nextTick()

        this.setQuickAddProduct(this.selectedProduct)
        this.setSizeGuideProduct(this.finalProduct)
      }

      this.setInitialActiveMerge()
    },

    /**
     * Handle scrolling product carousel.
     * @param {String} direction - Direction to move carousel of sign post.
     */
    moveProductCarousel(direction) {
      const carousel = this.$refs.carousel
      const cards = this.$refs.cards

      if (!carousel || !cards) {
        return
      }

      const card = cards[0].$el
      const cardWidth = card.clientWidth

      this.currentCarouselIndex = direction === 'prev'
        ? Math.max(0, this.currentCarouselIndex - 1)
        : Math.min(cards.length, this.currentCarouselIndex + 1)

      carousel.scrollLeft = this.currentCarouselIndex * cardWidth
    },

    /**
     * Get product images.
     * @param {Object} product
     * @returns {Array}
     */
    getProductImages(product) {
      if (product?.images && product?.images?.length > 0) {
        return product.images
      }

      if (product?.media && product?.media?.length > 0) {
        return product.media?.filter(val => val.mediaType === 'image').map(val => val.image)
      }

      return []
    },

    /**
     * Handles carousel scroll event to update arrows.
     * @param {Boolean} [scrolled] - Method triggered by scroll event.
     */
    handleCarouselScroll(scrolled) {
      const carousel = this.$refs.carousel
      const cards = this.$refs.cards

      if (!carousel || !cards) {
        return
      }

      if (scrolled) {
        this.interactedWith = true
      }

      const scrollWidth = carousel.scrollWidth
      const scroll = carousel.scrollLeft

      const card = cards[0].$el
      const cardWidth = card.clientWidth

      const prevButton = this.$refs.prev
      const nextButton = this.$refs.next

      const hideButton = (button) => {
        button?.classList.add(this.$class('disabled'))
        button?.setAttribute('disabled', true)
      }

      const showButton = (button) => {
        button?.classList.remove(this.$class('disabled'))
        button?.removeAttribute('disabled')
      }

      if (scroll < (cardWidth * 0.5) || scroll === 0 || cardWidth === 0) {
        hideButton(prevButton)
      } else {
        showButton(prevButton)
      }

      if ((scroll + (cardWidth * 1.5)) >= scrollWidth && scroll !== 0 && cardWidth !== 0) {
        hideButton(nextButton)
      } else {
        showButton(nextButton)
      }
    },

    /**
     * Opens the QATC if configured, otherwise lets the link go through
     * @param event - The click event
     */
    handleImageHrefClick() {
      if (
        this.imageIsLinked ||
        !((this.shopifyProduct || this.product) && this.showInfo && this.showQuickAdd)
      ) {
        this.handleHrefClick()
        return
      }

      this.handleAtcEvent()
    },

    /**
     * Handles secondary image display
     */
    handleSecondaryImage() {
      if (!this.enableSecondaryImage) {
        return
      }

      this.isCardSelected = true
      this.loadSecondaryImage = true
    },

    /**
     * Handle selected product update.
     * @param {String} id - Product ID.
     */
    handleUpdateProductId(id) {
      if (this.finalProduct?.id === id?.toString().replace('gid://shopify/Product/', '')) {
        return
      }

      this.fetchShopifyProduct(id, true)
    },

    /**
     * Handle merge update.
     * - In product cards this is only ever a new group, not an extension.
     * - If legacy type filter images to new selected variant.
     * - Otherwise do nothing as product ID update will load new product.
     * @param {Object} group - Updated group object.
     */
    handleUpdateMerge(merge) {
      this.activeMerge = merge

      if (this.$refs.carousel) {
        this.$refs.carousel.scroll({
          behavior: 'instant',
          left: 0,
          top: 0,
        })
      }

      if (merge?.type !== 'legacy') {
        this.legacyGroup = false
        return
      }

      this.legacyGroup = merge
      const productImages = this.getProductImages(this.finalProduct)

      const filteredImages = productImages.filter((image) => {
        return image?.alt?.includes(' - ')
      })

      const selectedVariant = this.finalProduct?.variants?.find((variant) => {
        return variant.title.includes(this.activeMergeName)
      })

      this.selectedProduct = {
        ...this.selectedProduct,
        featuredImage: filteredImages.length
          ? filteredImages[0]
          : this.selectedProduct.featuredImage,
        groupName: this.activeMergeName,
        images: filteredImages?.length
          ? filteredImages
          : productImages,
        price: selectedVariant
          ? selectedVariant.price
          : this.selectedProduct?.price,
        secondaryImage: filteredImages?.length > 1
          ? filteredImages[1]
          : null,
      }
    },

    /**
     * Set initial active merge object.
     */
    setInitialActiveMerge() {
      if (!this.finalProduct || this.finalProduct.loading) {
        return
      }

      const activeMerge = setInitialActiveMerge(this.finalProduct, this.legacyGroup)

      if (!activeMerge) {
        return
      }

      this.activeMerge = activeMerge

      /**
       * Update filter image view and selected product.
       */
      const productImages = this.getProductImages(this.finalProduct)

      const filteredImages = productImages?.filter(image => {
        return image?.alt?.includes(' - ')
      })

      this.selectedProduct = {
        ...this.finalProduct,
        featuredImage: filteredImages?.length
          ? filteredImages[0]
          : this.finalProduct?.featuredImage,
        groupName: this.activeMerge?.swatch?.name,
        images: filteredImages?.length
          ? filteredImages
          : productImages,
        secondaryImage: filteredImages?.length > 1
          ? filteredImages[1]
          : null,
      }
    },

    /**
     * Handles back in stock modal display
     * @param {Boolean} isActive - back in stock display state
     */
    handleBackInStockModal(isActive) {
      this.showBackInStock = isActive
    },

    /**
     * Handles mouse leave on product card
     */
    handleMouseLeave() {
      this.isCardSelected = false
      this.showAddToCart = false
      this.showBackInStock = false
    },

    /**
     * Make the quick add available on focus
     */
    handleFocusIn() {
      if (!this.isMobile) {
        this.isCardSelected = true
      }
    },

    /**
     * Hide quick add when focus leaves the card
     * @param $event The focus event
     */
    handleFocusOut($event) {
      if (!this.$refs.productCard.contains($event.relatedTarget)) {
        this.handleMouseLeave()
      }
    },

    /**
     * Checks if product was added to cart, updates add to cart button icon
     */
    checkProductWasAdded() {
      if (!this.lastAddedProductId) {
        this.productWasAddedToCart = false

        return
      }

      this.productWasAddedToCart = this.finalProduct?.variants?.some((variant) => {
        return variant.id === this.lastAddedProductId
      })

      if (this.productWasAddedToCart) {
        this.showAddToCart = false
      }
    },

    /**
     * Handles click on add to cart button
     */
    handleAtcEvent() {
      this.refetchShopifyProduct()

      if (this.showAddToCart) {
        this.showAddToCart = false
        return
      }

      this.setQuickAddProduct(this.selectedProduct)
      this.setSizeGuideProduct(this.finalProduct)

      if (this.showUpdatedSuccess) {
        this.setShowUpdatedSuccess(true)
      }

      if (this.customAtc) {
        this.$emit('custom-quick-add')
        return
      }

      this.showAddToCart = true
      this.addToCartOverlayOpen = true

      this.openOverlay({
        ignoreDismissed: true,
        namespace: 'quick-add-to-cart-modal',
      })
    },

    /**
     * Handle dynamic yield recommendations.
     */
    handleDynamicYieldOverlay() {
      cnvs.EventBus.emit('dy:bis')
    },

    /**
     * Load data on when element becomes visible
     */
    setDataLoadObserver() {
      if (this.getShopifyProduct) {
        const dataLoadTriggered = this.checkIfVisible()

        if (!dataLoadTriggered) {
          window.addEventListener(
            'scroll',
            this.checkIfVisible,
            { passive: true },
          )
        }
      }
    },

    /**
     * Check if the root element is in view.
     * If so, trigger data fetch.
     * @returns {Boolean}
     */
    checkIfVisible() {
      if (
        !this.intersected &&
        isVisible(this.$el, {
          offsetEnd: window.innerHeight || 600,
          offsetStart: window.innerHeight || 600,
        })
      ) {
        this.intersected = true
        this.fetchShopifyProduct()
        return true
      }

      return false
    },

    /**
     * Sets history for tracking purposes in local storage of
     * card location and metaobject name
     */
    handleHrefClick() {
      const trackingHistory = {}
      if (this.productCardLocation?.key) {
        trackingHistory.pageSource = this.productCardLocation.key
      }

      if (this.productCardLocation?.metaobjectName) {
        trackingHistory.metaobjectName = this.productCardLocation.metaobjectName
      }

      localStorage.setItem('trackingHistory', JSON.stringify(trackingHistory))
    },

    /**
     * Emit product clicked
     */
    emitProductClick() {
      this.handleHrefClick()

      if (this.source !== 'five-block-section' && !this.imageIsLinked) {
        this.handleAtcEvent()
      }

      window.cnvs.EventBus.emit('cnvs:dy-product-card', this.product)
    },

    getTitleClasses() {
      let classes = ''

      if (cnvs.page.type === 'collection') {
        classes = 'font-family-freight'
      }

      if (this.fontSettings.length) {
        return `${classes} ${this.fontSettings}`
      }

      return `${classes} text-body-2xs-regular align-${this.cardContentAlignment}`
    },

    isShippingUnavailable() {
      if (cnvs?.settings?.blockedProduct &&
        this.selectedProduct.tags?.includes('international: block') &&
        Shopify.country !== 'US') {
        return true
      }

      return false
    },
  },
}
</script>

<style lang="scss">
@import './product-card';
</style>
